import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { GalleryTheme } from '../types';

interface ThemeListProps {
  themes: GalleryTheme[];
  year: string;
}

function ThemeCard({ theme, year }: { theme: GalleryTheme; year: string }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const showImage = theme.thumbnailUrl && !imageError;

  return (
    <Link
      key={theme.themeId}
      to={`/${year}/theme/${theme.themeId}`}
      state={{ themeTitle: theme.title }}
      className="block rounded-lg border border-gray-200 overflow-hidden hover:border-sky-300 hover:shadow-sm transition-all"
    >
      {showImage && (
        <div className="relative w-full aspect-[3/2]">
          {!imageLoaded && (
            <div
              data-testid="thumbnail-skeleton"
              className="absolute inset-0 bg-gray-200 animate-pulse"
            />
          )}
          <img
            src={theme.thumbnailUrl}
            alt={`${theme.title} theme thumbnail`}
            loading="lazy"
            className={`w-full h-full object-cover ${imageLoaded ? '' : 'opacity-0'}`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <h2 className="text-lg font-semibold text-gray-900">
            {theme.title}
          </h2>
          {theme.type === 'official' && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
              Official Photos
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <time dateTime={theme.date}>
            {new Date(theme.date + 'T00:00:00').toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </time>
          <span>·</span>
          <span>
            {theme.publishedPhotoCount}{' '}
            {theme.publishedPhotoCount === 1 ? 'photo' : 'photos'}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function ThemeList({ themes, year }: ThemeListProps) {
  if (themes.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {themes.map((theme) => (
        <ThemeCard key={theme.themeId} theme={theme} year={year} />
      ))}
    </div>
  );
}
